import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";
import * as XLSX from 'xlsx';

import {
  Button,
  CardActions,
  Chip,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Box
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import {
  ADMIN_AML_CHECK_RESULTS,
  AML_VERIFICATION_PPS_ACTIONS,
  AmlCheckResult,
  AmlMatchReason,
  AmlMatchWithFullReport,
  WatchlistFullReport,
  formatCategorisations,
  formatCoolingOffLabel
} from "./amlTypes";
import {
  AML_BULK_PPS_MAX,
  ParseBulkUploadError,
  parseBulkUploadFile,
} from "./parse-bulk-aml-upload";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  card: {
    minWidth: 300,
    marginTop: "6em",
  },
  avatar: {
    margin: "1em",
    display: "flex",
    justifyContent: "center",
  },
  form: {
    padding: "0 1em 1em 1em",
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  tableCell: {
    padding:'0px 10px 5px 10px',
    border:'1px solid gray' 
  },
  tooltip: {
    padding: '0px'
  },
  resultsContainer: {
    marginTop: "2em",
    width: "100%",
    maxWidth: "1200px",
  },
  alignment: {
    margin: '0 auto',
    width: '100%'
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '2em',
    marginTop: '1em'
  },
  inputSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  orDivider: {
    fontSize: '1.2em',
    fontWeight: 'bold',
    color: '#666',
    padding: '0 1em'
  },
  fileUpload: {
    marginTop: '0.5em'
  },
  uploadStatus: {
    marginTop: '0.5em',
    color: 'green',
    fontSize: '0.9em'
  },
  noteSection: {
    marginTop: '1em',
    padding: '1em',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    maxWidth: '600px'
  },
  noteText: {
    fontSize: '0.85em',
    color: '#666',
    margin: '0.5em 0'
  },
  whiteSpace: {
    whiteSpace: 'pre-line'
  },
  watchlistSection: {
    marginTop: '1em',
    padding: '0.75em',
    backgroundColor: '#fff8e1',
    borderRadius: '4px',
    border: '1px solid #f0c36d'
  },
  watchlistHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.5em'
  },
  watchlistTitle: {
    fontWeight: 600,
    color: '#8a6d3b'
  },
  watchlistMatchCard: {
    padding: '0.75em',
    marginTop: '0.5em',
    backgroundColor: '#fff',
    border: '1px solid #e3d6a8',
    borderRadius: '4px'
  },
  watchlistMatchHeader: {
    fontWeight: 600,
    marginBottom: '0.25em'
  },
  watchlistLabel: {
    fontWeight: 500,
    color: '#555',
    minWidth: 170,
    display: 'inline-block'
  },
  watchlistRow: {
    fontSize: '0.85em',
    margin: '0.15em 0'
  },
  coolingOffBadge: {
    marginLeft: '0.5em'
  },
  matchDetailsCell: {
    whiteSpace: 'pre-wrap',
    maxWidth: 320,
    fontSize: '0.85em'
  },
  watchlistError: {
    color: '#c62828',
    fontStyle: 'italic'
  },
  fetchWatchlistButton: {
    marginLeft: '0.5em'
  },
  refreshAmlBar: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1em',
    padding: '0.5em 0.75em',
    marginBottom: '0.75em',
    backgroundColor: '#fff3e0',
    borderRadius: 4,
    border: '1px solid #ffcc80'
  },
  refreshAmlBarText: {
    fontSize: '0.8em',
    color: '#6d4c00',
    lineHeight: 1.35
  }
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

interface FormValues {
  EmailOrPpsAccount?: string | number;
}

const { Form } = withTypes<FormValues>();

// Surfaces a parsed Equifax Watchlist Full Report (Dow Jones EDD) for a single
// matched <watchlistEntry>. Backend returns `watchlist_reports[]` with one
// entry per match for true-match users; missing/errored reports show a notice.
type WatchlistMatchInput = AmlMatchReason | AmlMatchWithFullReport;

const WatchlistMatchCard = ({ match, classes }: { match: WatchlistMatchInput; classes: any }) => {
  const report: WatchlistFullReport | null = (match as AmlMatchWithFullReport).full_report ?? null;
  const reportError: string | undefined = (match as AmlMatchWithFullReport).full_report_error;

  const reasonHeader = [
    match.checkName || match.matchCategory || 'AML alert',
    match.name ? `- ${match.name}` : '',
    match.percentageMatch ? `(${match.percentageMatch}%)` : ''
  ].filter(Boolean).join(' ');

  if (reportError || !report) {
    return (
      <div className={classes.watchlistMatchCard}>
        <div className={classes.watchlistMatchHeader}>{reasonHeader}</div>
        <div className={classes.watchlistError}>
          {reportError || 'No Full Report available for this match yet. Click "Fetch Watchlist Details" to retrieve.'}
        </div>
      </div>
    );
  }

  const categorisationsLabel = formatCategorisations(report);

  const linkedPeps = (report.relativesAndAssociates || [])
    .map(r => `${r.associationName || 'unknown'} (${r.association || r.associationType || '—'})`)
    .filter(Boolean);

  const roles = (report.roles || [])
    .map(r => r.role ? `${r.role}${r.roleType ? ` [${r.roleType}]` : ''}` : '')
    .filter(Boolean);

  const addresses = (report.addresses || [])
    .map(a => {
      if (a.raw) return a.raw;
      return [a.addressLine1, a.city, a.region, a.postcode, a.country].filter(Boolean).join(', ');
    })
    .filter(Boolean);

  const coolingOff = report.coolingOff;
  const coolingOffLabel = formatCoolingOffLabel(coolingOff);
  const coolingOffColor: 'default' | 'primary' | 'secondary' =
    coolingOff && coolingOff.pastCoolingOff === false ? 'secondary' : 'default';

  return (
    <div className={classes.watchlistMatchCard}>
      <div className={classes.watchlistMatchHeader}>
        {reasonHeader}
        {report.cached && (
          <Chip label="cached" size="small" style={{ marginLeft: 8 }} />
        )}
      </div>

      <div className={classes.watchlistRow}>
        <span className={classes.watchlistLabel}>Matched name:</span>
        {report.matchedName || '-'} {report.gender ? `(${report.gender})` : ''}
      </div>

      <div className={classes.watchlistRow}>
        <span className={classes.watchlistLabel}>Category:</span>
        {categorisationsLabel}
      </div>

      {report.djDob && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>DOB on Dow Jones:</span>
          {report.djDob}
        </div>
      )}

      {(report.citizenship || report.jurisdiction || report.residentOf) && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>Country:</span>
          {[
            report.citizenship && `citizenship: ${report.citizenship}`,
            report.jurisdiction && `jurisdiction: ${report.jurisdiction}`,
            report.residentOf && `resident: ${report.residentOf}`
          ].filter(Boolean).join('; ')}
        </div>
      )}

      {linkedPeps.length > 0 && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>Linked PEPs / associates:</span>
          {linkedPeps.join('; ')}
        </div>
      )}

      {roles.length > 0 && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>Roles:</span>
          {roles.slice(0, 5).join('; ')}{roles.length > 5 ? `  (+${roles.length - 5} more)` : ''}
        </div>
      )}

      {addresses.length > 0 && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>Addresses:</span>
          {addresses.slice(0, 3).join(' | ')}{addresses.length > 3 ? `  (+${addresses.length - 3} more)` : ''}
        </div>
      )}

      {coolingOff.inactiveAsOfDate && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>Inactive as of:</span>
          {coolingOff.inactiveAsOfDate}
          {coolingOff.inactiveAsOfType ? ` (${coolingOff.inactiveAsOfType})` : ''}
          {coolingOffLabel && (
            <Chip
              className={classes.coolingOffBadge}
              label={coolingOffLabel}
              color={coolingOffColor}
              size="small"
            />
          )}
        </div>
      )}

      <div className={classes.watchlistRow}>
        <span className={classes.watchlistLabel}>DJ last updated:</span>
        {report.djLastUpdatedDate || '-'}
        {report.djActiveStatus ? `  |  status: ${report.djActiveStatus}` : ''}
        {report.djDeceased ? `  |  deceased: ${report.djDeceased}` : ''}
      </div>

      {report.djUniqueIdentifier && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>DJ identifier:</span>
          {report.djUniqueIdentifier}
        </div>
      )}

      {(report.imageUrls || []).length > 0 && (
        <div className={classes.watchlistRow}>
          <span className={classes.watchlistLabel}>Image references:</span>
          {(report.imageUrls || []).slice(0, 3).map((url: string, idx: number) => (
            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
              link {idx + 1}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

interface ResultRowProps {
  row: AmlCheckResult;
  // Replaces this row in the parent table with a fresh server response. Fired
  // by both the "Fetch Watchlist Details" and "Re-run AML" flows since both
  // return the same AmlCheckResult shape.
  onRowUpdated: (updatedRow: AmlCheckResult) => void;
}

const ResultRow = ({ row, onRowUpdated }: ResultRowProps) => {
  const classes = useStyles();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [fetchingWatchlist, setFetchingWatchlist] = useState(false);
  const [refreshingAml, setRefreshingAml] = useState(false);

  // Backend's fetchUserByEmailOrPpsOrId accepts pps_account_number, email, or
  // id. We prefer pps_account_number when the row has a real one (some users
  // have no PPS account → fetchPpsAccountNumber returns 'N/A'); otherwise we
  // fall back to email.
  const buildUserParam = (): { pps_account_number: string } | { email: string } | null => {
    const hasPps = !!row.pps_account_number && row.pps_account_number !== 'N/A';
    const hasEmail = !!row.email && row.email !== 'N/A';
    if (hasPps) return { pps_account_number: row.pps_account_number };
    if (hasEmail) return { email: row.email };
    return null;
  };

  const hasFlags = !!row.aml_flags && row.aml_flags !== 'False Match' && !String(row.aml_flags).startsWith('Error');
  const matchReasons: AmlMatchReason[] = Array.isArray(row.match_reasons) ? row.match_reasons : [];
  const watchlistReports: AmlMatchWithFullReport[] = Array.isArray(row.watchlist_reports) ? row.watchlist_reports : [];
  const hasWatchlistReports = watchlistReports.some(r => r && r.full_report);
  // Show the on-demand fetch button for ANY true-match row (any summary flag
  // set to true), not just rows where match_reasons is already populated.
  // Some older AML rows in DB were captured before the request asked Equifax
  // for the watchlistCheckOutcomes block, so match_reasons can be empty even
  // when standardRiskPep / sanctions / etc. is true. The button lets the
  // admin trigger a Full Report fetch in that case.
  const needsWatchlistFetch = hasFlags && !hasWatchlistReports;

  const handleFetchWatchlistDetails = () => {
    const userParam = buildUserParam();
    if (!userParam) {
      notify('Cannot fetch watchlist details without a PPS account number or email', 'warning');
      return;
    }
    setFetchingWatchlist(true);
    notify('Fetching watchlist Full Report from Equifax...', 'info');
    const config = getConfigGet({
      ...userParam,
      action: AML_VERIFICATION_PPS_ACTIONS.WATCHLIST_DETAILS
    });
    axios.get<AmlCheckResult[]>(ENDPOINTS.AML_VERIFICTION_PPS, config)
      .then(res => {
        setFetchingWatchlist(false);
        if (res.data && Array.isArray(res.data) && res.data[0]) {
          onRowUpdated(res.data[0]);
          notify('Watchlist Full Report fetched', 'info', '', undefined, 5000);
          setOpen(true);
        } else {
          notify('No watchlist data returned', 'warning');
        }
      })
      .catch((error) => {
        setFetchingWatchlist(false);
        notify(error?.response?.data?.message || 'Failed to fetch watchlist details', 'error', '', undefined, 5000);
      });
  };

  // Re-runs the entire Equifax AML check for this user. Costs one paid Equifax
  // call and writes a new aml_verification row on the backend (old rows are
  // kept for audit). Confirms first because the verdict can flip and any new
  // true matches will trigger their own Watchlist Full Report prefetch.
  const handleRefreshAml = () => {
    const userParam = buildUserParam();
    if (!userParam) {
      notify('Cannot re-run AML without a PPS account number or email', 'warning');
      return;
    }
    const ok = window.confirm(
      'Re-run AML check for this user?\n\n' +
      'This calls Equifax (one paid call), inserts a fresh aml_verification ' +
      'row, and updates the user\'s verdict. The previous row is kept for ' +
      'audit. Any new true matches will have their Watchlist Full Reports ' +
      'fetched automatically.'
    );
    if (!ok) return;

    setRefreshingAml(true);
    notify('Re-running Equifax AML check...', 'info');
    const config = getConfigGet({
      ...userParam,
      action: AML_VERIFICATION_PPS_ACTIONS.REFRESH_AML
    });
    axios.get<AmlCheckResult[]>(ENDPOINTS.AML_VERIFICTION_PPS, config)
      .then(res => {
        setRefreshingAml(false);
        if (res.data && Array.isArray(res.data) && res.data[0]) {
          const updated = res.data[0];
          onRowUpdated(updated);
          const flagsLabel = updated.aml_flags && updated.aml_flags !== 'False Match'
            ? `flags: ${String(updated.aml_flags).replace(/\n/g, ', ')}`
            : 'no flags';
          notify(`AML refresh completed (${flagsLabel})`, 'info', '', undefined, 6000);
          setOpen(true);
        } else {
          notify('AML refresh returned no data', 'warning');
        }
      })
      .catch((error) => {
        setRefreshingAml(false);
        notify(error?.response?.data?.message || 'Failed to re-run AML', 'error', '', undefined, 5000);
      });
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.date}</TableCell>
        <TableCell>{row.pps_account_number}</TableCell>
        <TableCell>{row.email}</TableCell>
        <TableCell>{row.full_name}</TableCell>
        <TableCell>{row.dob}</TableCell>
        <TableCell>{row.address}</TableCell>
        <TableCell style={{ whiteSpace: 'pre-wrap', color: hasFlags ? '#b71c1c' : 'inherit', fontWeight: hasFlags ? 600 : 'normal' }}>
          {row.aml_flags}
        </TableCell>
        <TableCell className={classes.matchDetailsCell}>
          {row.match_details || (hasFlags ? '(no match details)' : '')}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <div className={classes.refreshAmlBar}>
                <Typography variant="caption" className={classes.refreshAmlBarText}>
                  <strong>Re-run AML:</strong> calls Equifax (1 paid call) and inserts a fresh
                  {' '}aml_verification row. The previous row is kept for audit. Any new true
                  {' '}matches will have their Watchlist Full Reports prefetched automatically.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  disabled={refreshingAml || fetchingWatchlist}
                  onClick={handleRefreshAml}
                >
                  {refreshingAml && <CircularProgress size={14} thickness={2} style={{ marginRight: 6 }} />}
                  Re-run AML
                </Button>
              </div>

             <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell style={{ color: row.sanctions === 'true' ? 'red' : 'inherit', fontWeight: row.sanctions === 'true' ? 'bold' : 'normal' }}>Sanctions:</TableCell>
                    <TableCell style={{ color: row.sanctions === 'true' ? 'red' : 'inherit', fontWeight: row.sanctions === 'true' ? 'bold' : 'normal' }}>{row.sanctions}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ color: row.highRiskPep === 'true' ? 'red' : 'inherit', fontWeight: row.highRiskPep === 'true' ? 'bold' : 'normal' }}>High Risk PEP:</TableCell>
                    <TableCell style={{ color: row.highRiskPep === 'true' ? 'red' : 'inherit', fontWeight: row.highRiskPep === 'true' ? 'bold' : 'normal' }}>{row.highRiskPep}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ color: row.standardRiskPep === 'true' ? 'red' : 'inherit', fontWeight: row.standardRiskPep === 'true' ? 'bold' : 'normal' }}>Standard Risk PEP:</TableCell>
                    <TableCell style={{ color: row.standardRiskPep === 'true' ? 'red' : 'inherit', fontWeight: row.standardRiskPep === 'true' ? 'bold' : 'normal' }}>{row.standardRiskPep}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ color: row.highRiskSip === 'true' ? 'red' : 'inherit', fontWeight: row.highRiskSip === 'true' ? 'bold' : 'normal' }}>High Risk SIP:</TableCell>
                    <TableCell style={{ color: row.highRiskSip === 'true' ? 'red' : 'inherit', fontWeight: row.highRiskSip === 'true' ? 'bold' : 'normal' }}>{row.highRiskSip}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ color: row.standardRiskSip === 'true' ? 'red' : 'inherit', fontWeight: row.standardRiskSip === 'true' ? 'bold' : 'normal' }}>Standard Risk SIP:</TableCell>
                    <TableCell style={{ color: row.standardRiskSip === 'true' ? 'red' : 'inherit', fontWeight: row.standardRiskSip === 'true' ? 'bold' : 'normal' }}>{row.standardRiskSip}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ color: row.highRiskRca === 'true' ? 'red' : 'inherit', fontWeight: row.highRiskRca === 'true' ? 'bold' : 'normal' }}>High Risk RCA:</TableCell>
                    <TableCell style={{ color: row.highRiskRca === 'true' ? 'red' : 'inherit', fontWeight: row.highRiskRca === 'true' ? 'bold' : 'normal' }}>{row.highRiskRca}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ color: row.standardRiskRca === 'true' ? 'red' : 'inherit', fontWeight: row.standardRiskRca === 'true' ? 'bold' : 'normal' }}>Standard Risk RCA:</TableCell>
                    <TableCell style={{ color: row.standardRiskRca === 'true' ? 'red' : 'inherit', fontWeight: row.standardRiskRca === 'true' ? 'bold' : 'normal' }}>{row.standardRiskRca}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ color: row.vulnerable === 'true' ? 'red' : 'inherit', fontWeight: row.vulnerable === 'true' ? 'bold' : 'normal' }}>Vulnerable:</TableCell>
                    <TableCell style={{ color: row.vulnerable === 'true' ? 'red' : 'inherit', fontWeight: row.vulnerable === 'true' ? 'bold' : 'normal' }}>{row.vulnerable}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {hasFlags && (
                <div className={classes.watchlistSection}>
                  <div className={classes.watchlistHeader}>
                    <Typography variant="subtitle2" className={classes.watchlistTitle}>
                      {watchlistReports.length || matchReasons.length
                        ? `Watchlist Full Reports (${watchlistReports.length || matchReasons.length} match${(watchlistReports.length || matchReasons.length) === 1 ? '' : 'es'})`
                        : 'Watchlist Full Reports'}
                    </Typography>
                    {needsWatchlistFetch && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        disabled={fetchingWatchlist || refreshingAml}
                        onClick={handleFetchWatchlistDetails}
                      >
                        {fetchingWatchlist && <CircularProgress size={14} thickness={2} style={{ marginRight: 6 }} />}
                        Fetch Watchlist Details
                      </Button>
                    )}
                  </div>

                  {row.watchlist_summary && (
                    <Typography variant="body2" style={{ marginBottom: '0.5em', fontStyle: 'italic' }}>
                      {row.watchlist_summary}
                    </Typography>
                  )}

                  {(watchlistReports.length > 0 ? watchlistReports : matchReasons).length > 0 ? (
                    (watchlistReports.length > 0 ? watchlistReports : matchReasons).map((match: any, idx: number) => (
                      <WatchlistMatchCard key={idx} match={match} classes={classes} />
                    ))
                  ) : (
                    <Typography variant="body2" className={classes.watchlistError}>
                      No per-match details on file for this AML row. Click "Fetch Watchlist Details" to retrieve the Full Report from Equifax.
                      If the fetch returns empty, the cached AML response was captured before the request asked for watchlist outcomes — re-running AML for this user will populate it.
                    </Typography>
                  )}
                </div>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AmlChecks = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [checkLoading, setCheckLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [amlResults, setAmlResults] = useState<AmlCheckResult[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [ppsAccounts, setPpsAccounts] = useState<string[]>([]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setAmlResults([]);
    setPpsAccounts([]);

    try {
      const parsed = await parseBulkUploadFile(file);
      setPpsAccounts(parsed.pps_accounts);

      const extras: string[] = [];
      if (parsed.duplicate_count > 0) {
        extras.push(`${parsed.duplicate_count} duplicate row(s) included`);
      }
      if (parsed.dropped_invalid > 0) {
        extras.push(`${parsed.dropped_invalid} invalid cell(s) ignored`);
      }
      parsed.warnings.forEach((w) => extras.push(w));

      const suffix = extras.length ? ` — ${extras.join("; ")}` : "";
      notify(
        `✓ ${parsed.pps_accounts.length} PPS account numbers uploaded${suffix}`,
        "info",
      );
    } catch (error) {
      setUploadedFile(null);
      setPpsAccounts([]);
      if (error instanceof ParseBulkUploadError) {
        notify(error.message, "error");
      } else {
        notify("Failed to parse upload file", "error");
      }
    }

    input.value = "";
  };
  
  const handleCheckAml = (inputData: any) => {
    setAmlResults([]);
    setCheckLoading(true);
    notify("Fetching AML check results...", "info");

    if (ppsAccounts.length > 0 && uploadedFile) {
      // Send parsed PPS accounts directly
      const config = getConfigGet({
        pps_accounts: ppsAccounts,
        action: AML_VERIFICATION_PPS_ACTIONS.BULK_CHECK
      });

      axios.get<AmlCheckResult[]>(ENDPOINTS.AML_VERIFICTION_PPS, config)
        .then(res => {
          if (res.data && Array.isArray(res.data)) {
            setAmlResults(res.data);
            const flaggedCount = res.data.filter(r => r.aml_flags && r.aml_flags !== 'False Match').length;
            notify(`AML check completed: ${res.data.length} users checked (${flaggedCount} with flags)`, "info", "", undefined, 5000);
          } else {
            setAmlResults([]);
          }
          setCheckLoading(false);
        })
        .catch((error) => {
          setCheckLoading(false);
          notify(error?.response?.data?.message || 'Failed to get bulk AML check results', "error", "", undefined, 5000);
        });
    } else {
      const { EmailOrPpsAccount } = inputData;
      let config;

      if (isNaN(Number(EmailOrPpsAccount))) {
        config = getConfigGet({ email: EmailOrPpsAccount, action: AML_VERIFICATION_PPS_ACTIONS.CHECK });
      } else {
        config = getConfigGet({ pps_account_number: EmailOrPpsAccount, action: AML_VERIFICATION_PPS_ACTIONS.CHECK });
      }

      axios.get<AmlCheckResult[]>(ENDPOINTS.AML_VERIFICTION_PPS, config)
      .then(res => {
          if (res.data && Array.isArray(res.data)) {
            setAmlResults(res.data);
        } else {
          setAmlResults([]);
        }
        setCheckLoading(false);
        notify("AML check completed successfully", "info", "", undefined, 5000);
      })
      .catch((error) => {
        setCheckLoading(false);
        notify(error?.response?.data?.message || 'Failed to get AML check results', "error", "", undefined, 5000);
      });
    }
  };

  const handleDownloadAml = (inputData: any) => {
    setDownloadLoading(true);
    notify("Preparing AML download...", "info");

    if (ppsAccounts.length > 0 && uploadedFile) {
      // Send parsed PPS accounts directly
      const config = getConfigGet({
        pps_accounts: ppsAccounts,
        action: AML_VERIFICATION_PPS_ACTIONS.BULK_DOWNLOAD
      });

      axios.get<Array<{ results: AmlCheckResult[]; downloadRows: Record<string, any>[] }>>(ENDPOINTS.AML_VERIFICTION_PPS, config)
        .then(res => {
          if (res.data && res.data[0]) {
            const { results, downloadRows } = res.data[0];

            if (downloadRows && Array.isArray(downloadRows)) {
              // Column order: identity fields, then match + watchlist (see amlTypes).
              const ws = XLSX.utils.json_to_sheet(downloadRows, {
                header: Object.values(ADMIN_AML_CHECK_RESULTS)
              });
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'AML Results');
              const today = new Date().toISOString().split('T')[0];
              XLSX.writeFile(wb, `AML_Bulk_Check_${today}.xlsx`);
            }

            if (results && Array.isArray(results)) {
              setAmlResults(results);
            }

            setDownloadLoading(false);
            notify('Bulk AML results downloaded successfully', "info", "", undefined, 5000);
          }
        })
        .catch((error) => {
          setDownloadLoading(false);
          notify(error?.response?.data?.message || 'Failed to download bulk AML results', "error", "", undefined, 5000);
        });
    } else {
      const { EmailOrPpsAccount } = inputData;
      let config;

      if (isNaN(Number(EmailOrPpsAccount))) {
        config = getConfigGet({ email: EmailOrPpsAccount, action: AML_VERIFICATION_PPS_ACTIONS.DOWNLOAD });
      } else {
        config = getConfigGet({ pps_account_number: EmailOrPpsAccount, action: AML_VERIFICATION_PPS_ACTIONS.DOWNLOAD });
      }

      axios.get(ENDPOINTS.AML_VERIFICTION_PPS, config)
      .then((res: any) => {
        let fileData = 
          "Name: " + res.data[0] + "\n" +
          "Date of Birth: " + res.data[1] + "\n" +
          "Address: " + res.data[2] + "\n" +
          "Aml xml: " + res.data[3];

        const element = document.createElement("a");
        const file = new Blob([fileData], { type: 'text/plain;charset=utf-8' });
        element.href = URL.createObjectURL(file);
        element.download = res.data[0] + "_aml.txt";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        setDownloadLoading(false);
        notify('AML verification file downloaded successfully', "info", "", undefined, 5000);
      })
      .catch((error) => {
        setDownloadLoading(false);
        notify(error?.response?.data?.message || 'Failed to download AML verification file', "error", "", undefined, 5000);
      });
    }
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    return errors;
  };

  const hasInput = (values: FormValues) => {
    return (values.EmailOrPpsAccount && values.EmailOrPpsAccount.toString().trim() !== '') || ppsAccounts.length > 0;
  };

  const isInputAvailable = () => {
    return ppsAccounts.length > 0;
  };

  return (
    <>
      <Form
        onSubmit={() => {}}
        validate={validate}
        render={({ values }) => (
          <div>
            <div className={classes.main}>
              <div className={classes.form}>
                  <div className={classes.inputContainer}>
                    <div className={classes.inputSection}>
                  <Field
                    autoFocus
                    name="EmailOrPpsAccount"
                    component={renderInput}
                        label=""
                        placeholder="Enter email or PPS account number"
                        disabled={checkLoading || downloadLoading || ppsAccounts.length > 0}
                  />
                    </div>

                  <div className={classes.orDivider}>
                    OR
                  </div>

                  <div className={classes.inputSection}>
                    <div className={classes.fileUpload}>
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                        disabled={checkLoading || downloadLoading || (values.EmailOrPpsAccount ? true : false)}
                      />
                      {uploadedFile && (
                        <div className={classes.uploadStatus}>
                          ✓ {uploadedFile.name} ({ppsAccounts.length} PPS numbers)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <CardActions className={classes.actions} style={{ display: 'flex', gap: '10px' }}>
                <Button
                  variant="contained"
                  color='primary'
                  disabled={checkLoading || downloadLoading || (!hasInput(values) && !isInputAvailable())}
                  fullWidth
                  onClick={() => handleCheckAml(values)}
                >
                  {checkLoading && <CircularProgress size={25} thickness={2} />}
                  Check AML
                </Button>
                <Button
                  variant="contained"
                  color='secondary'
                  disabled={checkLoading || downloadLoading || (!hasInput(values) && !isInputAvailable())}
                  fullWidth
                  onClick={() => handleDownloadAml(values)}
                >
                  {downloadLoading && <CircularProgress size={25} thickness={2} />}
                  Download Results
                </Button>
              </CardActions>

              <div className={classes.noteSection}>
                <Typography variant="body2" className={classes.noteText}>
                  <strong>Note:</strong> Bulk upload accepts Excel or CSV. Use a prior AML export
                  (column &quot;PPS Account Number&quot;) or an Outstanding Referrals file — PPS
                  columns are detected automatically (12–16 digits, max {AML_BULK_PPS_MAX} accounts per run).
                </Typography>
              </div>

              <Notification />
            </div>

            {amlResults.length > 0 && (
              <div className={classes.resultsContainer}>
                <div className={classes.alignment}>
                  <Typography variant="h6" gutterBottom>
                    AML Check Results: {amlResults.length} user(s) checked
                    {' '}({amlResults.filter(r => r.aml_flags && r.aml_flags !== 'False Match' && !String(r.aml_flags).startsWith('Error')).length} with risk
                    {', '}
                    {amlResults.filter(r => Array.isArray(r.watchlist_reports) && r.watchlist_reports.some((wr: any) => wr && wr.full_report)).length} with watchlist Full Report
                    {', '}
                    {amlResults.filter(r => String(r.aml_flags || '').startsWith('Error')).length} errored)
                  </Typography>
                  
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.DATE}</TableCell>
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.PPS_ACCOUNT_NUMBER}</TableCell>
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.EMAIL}</TableCell>
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.FULL_NAME}</TableCell>
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.DOB}</TableCell>
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.ADDRESS}</TableCell>
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.AML_FLAGS}</TableCell>
                          <TableCell>{ADMIN_AML_CHECK_RESULTS.MATCH_DETAILS}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {amlResults.map((row, index) => (
                          <ResultRow
                            key={index}
                            row={row}
                            onRowUpdated={(updatedRow) => {
                              setAmlResults(prev => prev.map((r, i) => i === index ? { ...r, ...updatedRow } : r));
                            }}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            )}
          </div>
        )}
      />
    </>
  );
};


export default AmlChecks;
